
import React from 'react';

export default function MeasurementSection() {
  const metrics = [
    {
      title: 'Families Supported',
      description: 'Direct beneficiaries receiving comprehensive oxygen support',
      icon: 'ri-home-heart-line',
      futureTarget: '10,000+ families by Year 2'
    },
    {
      title: 'PHCs Enabled',
      description: 'Primary Health Centers equipped with OxyAI™ technology',
      icon: 'ri-hospital-line',
      futureTarget: '200+ centers by Year 3'
    },
    {
      title: 'OxyAI™ App Users',
      description: 'Individuals screened through our AI-powered assessment',
      icon: 'ri-smartphone-line',
      futureTarget: '1,00,000+ users by Year 3'
    },
    {
      title: 'Kits & Products Distributed',
      description: 'Complete oxygen supplementation packages delivered',
      icon: 'ri-medicine-bottle-line',
      futureTarget: '55,000+ kits by Year 3'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            How We Measure Impact
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Every rupee is tracked, every donation is accounted for.
          </p>
          <div className="bg-[#12B6B6]/5 rounded-2xl p-6 inline-block">
            <p className="text-lg font-medium text-[#111111]">
              "Transparency isn't just our commitment—it's our foundation."
            </p>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {metrics.map((metric, index) => (
            <div key={index} className="bg-[#FAFAFA] rounded-3xl p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-[#12B6B6] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className={`${metric.icon} text-2xl text-white`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#111111] mb-2">{metric.title}</h3>
                  <p className="text-gray-600 mb-4">{metric.description}</p>
                  <div className="bg-white rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">Future Target:</div>
                    <div className="text-lg font-semibold text-[#12B6B6]">{metric.futureTarget}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Future Dashboard Preview */}
        <div className="bg-gradient-to-r from-gray-50 to-[#12B6B6]/5 rounded-3xl p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Content */}
            <div>
              <h3 className="text-2xl font-bold text-[#111111] mb-6">Future Live Dashboard</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Once programs launch, donors and partners will have 24/7 access to real-time impact data 
                through our transparent reporting system.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line w-6 h-6 flex items-center justify-center text-[#12B6B6]"></i>
                  <span className="text-sm text-gray-700">Real-time beneficiary tracking</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line w-6 h-6 flex items-center justify-center text-[#12B6B6]"></i>
                  <span className="text-sm text-gray-700">Quarterly audit reports</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line w-6 h-6 flex items-center justify-center text-[#12B6B6]"></i>
                  <span className="text-sm text-gray-700">Geographic impact mapping</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line w-6 h-6 flex items-center justify-center text-[#12B6B6]"></i>
                  <span className="text-sm text-gray-700">Donor impact statements</span>
                </div>
              </div>
            </div>
            
            {/* Right Content - Dashboard Mockup */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-[#111111]">Impact Dashboard</h4>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                  Coming Soon
                </span>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gray-300">---</div>
                    <div className="text-xs text-gray-500">Families Supported</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gray-300">---</div>
                    <div className="text-xs text-gray-500">PHCs Enabled</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Program Progress</span>
                    <span className="text-sm text-gray-400">Launching Soon</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="w-0 h-2 bg-[#12B6B6] rounded-full transition-all duration-500"></div>
                  </div>
                </div>
                
                <div className="text-center py-4">
                  <i className="ri-bar-chart-line text-4xl text-gray-300 mb-2"></i>
                  <p className="text-xs text-gray-500">Live charts and analytics will appear here</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Accountability Promise */}
        <div className="mt-16 text-center bg-[#FAFAFA] rounded-3xl p-8">
          <h3 className="text-2xl font-bold text-[#111111] mb-4">Our Accountability Promise</h3>
          <p className="text-gray-700 max-w-3xl mx-auto mb-8">
            From day one, we commit to complete transparency. Every donation will be tracked, 
            every program outcome will be measured, and every impact will be verified.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <i className="ri-shield-check-line text-3xl text-[#12B6B6] mb-3"></i>
              <div className="text-sm font-medium text-[#111111] mb-1">80G Tax Exempt</div>
              <div className="text-xs text-gray-600">All donations are tax-deductible</div>
            </div>
            <div className="text-center">
              <i className="ri-file-shield-line text-3xl text-[#12B6B6] mb-3"></i>
              <div className="text-sm font-medium text-[#111111] mb-1">Quarterly Audits</div>
              <div className="text-xs text-gray-600">Independent financial verification</div>
            </div>
            <div className="text-center">
              <i className="ri-database-line text-3xl text-[#12B6B6] mb-3"></i>
              <div className="text-sm font-medium text-[#111111] mb-1">Open Data</div>
              <div className="text-xs text-gray-600">Public access to impact metrics</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
